import React, { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import {
  Building2, UserPlus, MoreHorizontal, X, Eye,
  Pause, Play, Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Tenant {
  id: number;
  name: string;
  status: string;
  domain: string;
  plan: string;
  userCount: number;
  invitedAt: string | null;
  createdAt: string;
}

function InviteTenantModal({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const res = await api.post("/v1/super-admin/tenants/invite", {
        email,
        companyName: companyName || undefined,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send invite");
      setSuccess(data.message || "Invitation sent!");
      setEmail("");
      setCompanyName("");
      onInvited();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold mb-4">Invite Company</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Admin email address
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              placeholder="admin@company.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company name <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              placeholder="Acme Corp"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          {success && <p className="text-sm text-green-600">{success}</p>}
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50"
            >
              {loading ? "Sending..." : "Send Invite"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TenantActions({
  tenant,
  onRefresh,
}: {
  tenant: Tenant;
  onRefresh: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleStatusChange(status: string) {
    setLoading(true);
    try {
      await api.patch(`/v1/super-admin/tenants/${tenant.id}/status`, { status });
      onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-50 bg-white rounded-xl shadow-lg border border-gray-200 py-1 min-w-[180px]">
            <Link
              to="/super-admin/tenants/$id"
              params={{ id: String(tenant.id) }}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setOpen(false)}
            >
              <Eye className="w-4 h-4" /> View Details
            </Link>
            {tenant.status === "active" ? (
              <button
                onClick={() => handleStatusChange("suspended")}
                disabled={loading}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
              >
                <Pause className="w-4 h-4" /> Suspend
              </button>
            ) : (
              <button
                onClick={() => handleStatusChange("active")}
                disabled={loading}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
              >
                <Play className="w-4 h-4" /> Activate
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const statusColors: Record<string, string> = {
  active: "bg-green-50 text-green-700",
  pending: "bg-amber-50 text-amber-700",
  suspended: "bg-red-50 text-red-700",
};

const planColors: Record<string, string> = {
  free: "bg-gray-100 text-gray-600",
  starter: "bg-blue-50 text-blue-700",
  pro: "bg-purple-50 text-purple-700",
  enterprise: "bg-indigo-50 text-indigo-700",
};

export function TenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  async function fetchTenants() {
    setLoading(true);
    try {
      const res = await api.get("/v1/super-admin/tenants");
      const data = await res.json();
      setTenants(data.tenants || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTenants();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tenants</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage companies on the platform
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Invite Company
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        ) : tenants.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <Building2 className="w-10 h-10 mb-3 text-gray-300" />
            <p>No tenants yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Invite a company to get started
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="w-12 px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tenants.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4">
                    <Link
                      to="/super-admin/tenants/$id"
                      params={{ id: String(t.id) }}
                      className="group"
                    >
                      <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                        {t.name.startsWith("Pending-") ? (
                          <span className="text-gray-400 italic">
                            Pending setup
                          </span>
                        ) : (
                          t.name
                        )}
                      </p>
                      {t.domain && (
                        <p className="text-xs text-gray-500">{t.domain}</p>
                      )}
                    </Link>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={cn(
                        "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                        statusColors[t.status] || "bg-gray-100 text-gray-600",
                      )}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={cn(
                        "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                        planColors[t.plan] || "bg-gray-100 text-gray-600",
                      )}
                    >
                      {t.plan}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {t.userCount}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(t.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <TenantActions tenant={t} onRefresh={fetchTenants} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <InviteTenantModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={fetchTenants}
      />
    </div>
  );
}
